'use client'
import { LegacyRef, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import {
  Avatar,
  Box,
  Flex,
  Stack,
  Text,
  Center,
  Spacer,
  Icon,
  Menu,
  MenuItem,
  MenuButton,
  MenuList,
  StackProps,
} from '@chakra-ui/react'
import { useDisclosure } from '@chakra-ui/hooks'
import { HiDotsHorizontal, HiOutlineChevronRight } from 'react-icons/hi'
import { BiLinkExternal } from 'react-icons/bi'
import dayjs from '@/lib/dayjs'
import reactStringReplace from 'react-string-replace'
import { FeedListItem } from '@/types/feed'
import { Images } from './Images'
import { SitePreview } from './SitePreview'
import { SocialShareAreaModal } from './SocialShareAreaModal'
import useUrl from '@/hooks/useUrl'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'

type CardProps = {
  feed: FeedListItem
  isLinkable: boolean
  shouldShowFull: boolean
} & StackProps

const BOX_HEIGHT = 140
const REPORT_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSe7PTqV2a7gBcGOB-B-aHLvomzwrznuU90fAM1_lL-mf8ovQQ/viewform'
const REGEXP_URL = /(https?:\/\/\S+)/g
const REGEXP_HASH = /(\s+|^)#[a-zA-Z0-9亜-熙ぁ-んァ-ヶー-龥朗-鶴.\-_]+/g
const REGEXP_URL_OR_HASH =
  /(https?:\/\/\S+|\s+#[a-zA-Z0-9亜-熙ぁ-んァ-ヶー-龥朗-鶴.\-_]+|^#[a-zA-Z0-9亜-熙ぁ-んァ-ヶー-龥朗-鶴.\-_]+)/g

type LinkableProps = {
  feed: FeedListItem
  isLinkable: boolean
  children: React.ReactNode
}
const Linkable = ({ feed, isLinkable, children }: LinkableProps) => {
  const searchParams = useSearchParams()
  if (isLinkable) {
    return <Link href={`/feeds/${feed.id}?${searchParams.toString()}`}>{children}</Link>
  }
  return <>{children}</>
}

export const Card = ({ feed, isLinkable = true, shouldShowFull = false, ...stackProps }: CardProps) => {
  const TextRef = useRef<HTMLParagraphElement>()
  const [isShowMoreBody] = useState(false)
  const [isShowMoreBodyBtn, setIsShowMoreBodyBtn] = useState(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const router = useRouter()
  const { host } = useUrl()

  useEffect(() => {
    if (!TextRef.current) return
    if (!shouldShowFull && TextRef.current.clientHeight >= BOX_HEIGHT) {
      setIsShowMoreBodyBtn(true)
    }
  }, [TextRef.current?.clientHeight, shouldShowFull])

  return (
    <Stack {...stackProps} direction={'row'} as="article">
      <Box
        px={2}
        py={4}
        w={'100%'}
        key={feed.id}
        borderBottomColor="border"
        borderBottomStyle="solid"
        borderBottomWidth="1px"
      >
        <Flex justifyContent={'space-between'}>
          <Link href={`/companies/${feed.branch_id}`}>
            <Flex alignItems={'center'} cursor="pointer">
              <Avatar mr={3} size={['sm', 'sm', 'md']} src={feed.company_thumbnail_url} />
              <Text fontSize={['sm', 'sm', 'md']} fontWeight={600}>
                {feed.company_name}
              </Text>
            </Flex>
          </Link>
          <Center>
            <Menu>
              <MenuButton>
                <Icon fontSize={['xl', 'xl', '2xl']} as={HiDotsHorizontal} color="gray.500" />
              </MenuButton>
              <MenuList>
                <MenuItem onClick={onOpen}>
                  <Text fontSize={['xs', 'xs', 'sm']}>この投稿をシェアする</Text>
                </MenuItem>
                <MenuItem color="red.500" onClick={() => window.open(REPORT_URL, '_blank')}>
                  <Text fontSize={['xs', 'xs', 'sm']} mr="2">
                    この投稿を報告する
                  </Text>
                  <Icon fontSize={['sm', 'sm', 'md']} as={BiLinkExternal} />
                </MenuItem>
              </MenuList>
            </Menu>
          </Center>
        </Flex>
        <Linkable feed={feed} isLinkable={isLinkable}>
          <Text
            ref={TextRef as LegacyRef<HTMLParagraphElement>}
            py={4}
            fontSize={['sm', 'sm', 'md']}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="pre-wrap"
            maxH={isShowMoreBody || shouldShowFull ? 'auto' : '140'}
          >
            {reactStringReplace(feed.body, REGEXP_URL_OR_HASH, (match, i) => {
              if (match.match(REGEXP_URL)) {
                return (
                  <Text
                    as="span"
                    className={'text-link'}
                    key={i}
                    onClick={(e) => {
                      e.preventDefault()
                      window.open(match, '_blank')
                    }}
                    color={'primary.500'}
                  >
                    {match}
                  </Text>
                )
              } else if (match.match(REGEXP_HASH)) {
                return (
                  <Text
                    key={i}
                    className={'text-link'}
                    cursor={'pointer'}
                    as={'span'}
                    color={'primary.500'}
                    onClick={(e) => {
                      e.preventDefault()
                      // NOTE: マルチバイト文字(？)がURLに含まれていると、server actionでエラーになるため、二重でエンコードしている
                      router.push(`/feeds?hashtag=1&q=${match.replace(/\n/g, '').replace(/#|　|\s/g, '')}`)
                    }}
                  >
                    {match}
                  </Text>
                )
              }
            })}
          </Text>
          {isShowMoreBodyBtn && !isShowMoreBody && (
            <Flex alignItems="center" mt={1}>
              <Spacer />
              <Text
                fontSize={['sm', 'sm', 'md']}
                cursor={'pointer'}
                className={'text-link'}
                color={'primary.500'}
                mr={1}
              >
                もっと見る
              </Text>
              <Icon fontSize={['xs', 'xs', 'sm']} color={'red.500'} as={HiOutlineChevronRight} />
            </Flex>
          )}
          {feed.images.length > 0 ? (
            <Images images={feed.images} />
          ) : (
            feed.site_preview && <SitePreview sitePreview={feed.site_preview} />
          )}
          <Box pt={1}>
            <Text fontSize={['xs', 'xs', 'sm']} color={'gray.500'}>
              {dayjs.unix(feed.created_at).fromNow()}
            </Text>
          </Box>
        </Linkable>
      </Box>
      <SocialShareAreaModal url={`${host}/feeds/${feed.id}`} isOpen={isOpen} onClose={onClose} />
    </Stack>
  )
}
